import { Route } from '@solidjs/router'
import { ErrorBoundary, Show, createSignal, onMount } from 'solid-js'
import { useAuth } from './lib/auth'
import { installStaleChunkRecovery } from './lib/pwa'
import Home from './routes/Home'
import Signup from './routes/Signup'
import Pass from './routes/Pass'
import History from './routes/History'
import Staff from './routes/Staff'
import VenueLogin from './routes/VenueLogin'
import VenueDashboard from './routes/VenueDashboard'
import Invite from './routes/Invite'
import Transfer from './routes/Transfer'

function isVenueDomain(): boolean {
  if (typeof window === 'undefined') return false
  return window.location.hostname === 'venue.zipcheck.my'
}

export default function App() {
  const auth = useAuth()
  const [isVenue, setIsVenue] = createSignal(false)

  onMount(() => {
    setIsVenue(isVenueDomain())
    installStaleChunkRecovery()
  })

  return (
    <ErrorBoundary fallback={(err) => (
      <div class="min-h-[calc(100dvh-2.5rem)] flex items-center justify-center p-6 text-center">
        <div>
          <p class="text-lg font-semibold text-gray-900 mb-2">Something went wrong</p>
          <p class="text-sm text-gray-600 mb-4">The app hit an unexpected error. Reloading usually fixes it.</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            class="px-4 py-2 rounded-lg bg-emerald-600 text-white font-medium"
          >
            Reload
          </button>
          <p class="mt-4 text-[11px] text-gray-400 break-all">{String(err)}</p>
        </div>
      </div>
    )}>
      <>
      {/* NOTE: no router primitives (useNavigate/useLocation/<A>) may live in this root
          layout — @solidjs/router allows them only inside a Route, and calling one here
          threw "use* router primitives can be only used inside a Route", killing the
          whole app. The mode switcher was removed for that reason: it must be rendered
          inside a Route component (it also used useLocation). */}
      {/* Always-visible way out. Without this a demo session was a one-way door: the
          token lives in localStorage, so closing and reopening the app returned you to
          the same screen with no route back to the start page. */}
      <Show when={auth.isAuthenticated()}>
        <div class="flex items-center justify-between px-4 py-2 bg-white/80 backdrop-blur border-b border-gray-200 text-sm">
          <span class="text-gray-500">
            {auth.mode() === 'staff' ? 'Staff · ' : ''}{auth.user()?.email}
          </span>
          <button
            type="button"
            // A full document load, not the router: @solidjs/router allows its
            // primitives (useNavigate and friends) only inside a Route component,
            // and this bar is part of the root layout — calling useNavigate here
            // threw "use* router primitives can be only used inside a Route" and
            // took the whole app down with an error boundary.
            onClick={() => { auth.logout(); window.location.assign('/') }}
            class="px-3 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Sign out
          </button>
        </div>
      </Show>

      {/* Routes — must be direct children of Router (no wrapper div) */}
      <Route path="/" component={Home} />
      <Route path="/signup" component={Signup} />
      <Route path="/pass" component={Pass} />
      <Route path="/history" component={History} />
      <Route path="/staff" component={Staff} />
      <Route path="/invite" component={Invite} />
      <Route path="/transfer" component={Transfer} />

      {/* Venue routes */}
      <Route path="/venue-login" component={VenueLogin} />
      <Route path="/venue/:venueId" component={VenueDashboard} />
      <Route path="/venue" component={VenueDashboard} />
      </>
    </ErrorBoundary>
  )
}
